import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BanknotesIcon, ArrowRightIcon, TrashIcon } from '@heroicons/react/24/outline';
import { listPayrollImports, deletePayrollImportById, type PayrollImportDocument } from '../services';
import { useToast } from '@/hooks/useToast';
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

export function PayrollExpensesPage() {
  const { success: showSuccess, error: showError } = useToast();
  const [imports, setImports] = useState<PayrollImportDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    const fetchImports = async () => {
      try {
        setLoading(true);
        const data = await listPayrollImports();
        setImports(data);
      } catch (err) {
        console.error('[PayrollExpensesPage] Erro ao carregar imports', err);
        showError('Não foi possível carregar as importações de folha.');
      } finally {
        setLoading(false);
      }
    };

    fetchImports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalsByImport = useMemo(() => {
    return imports.reduce<Record<string, { earnings: number; deductions: number }>>((acc, item) => {
      const earnings =
        typeof item.totals?.earning?.total === 'number'
          ? item.totals.earning.total
          : item.collaborators.reduce((sum, col) => sum + (col.earningsTotal ?? 0), 0);
      const deductions =
        typeof item.totals?.deduction?.total === 'number'
          ? item.totals.deduction.total
          : item.collaborators.reduce((sum, col) => sum + (col.deductionsTotal ?? 0), 0);

      acc[item.id] = { earnings, deductions };
      return acc;
    }, {});
  }, [imports]);

  const handleDelete = async (importId: string) => {
    const selected = imports.find((imp) => imp.id === importId);
    const label = selected?.competence ? ` da competência ${selected.competence}` : '';
    if (!window.confirm(`Remover a importação${label}? Esta ação não pode ser desfeita.`)) return;

    try {
      setDeletingId(importId);
      await deletePayrollImportById(importId);
      setImports((current) => current.filter((imp) => imp.id !== importId));
      showSuccess('Importação removida com sucesso.');
    } catch (err) {
      console.error('[PayrollExpensesPage] Erro ao remover import', err);
      showError('Não foi possível remover a importação.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <SubPageHeader
        backTo="/expenses"
        icon={<BanknotesIcon className="h-6 w-6" aria-hidden />}
        badge="Despesas"
        title="Folha"
        description="Área dedicada a despesas de folha de pagamento e importação de extratos."
        actions={
          <Link
            to="/expenses/payroll/import"
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700"
          >
            Importar
            <ArrowRightIcon className="h-4 w-4" aria-hidden />
          </Link>
        }
      />

      <div className="rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Importações recentes</h2>
        <p className="mt-1 text-sm text-gray-600">
          Competências importadas, totais de proventos e deduções e número de colaboradores mapeados.
        </p>

        {loading ? (
          <div className="mt-4 text-sm text-gray-500">Carregando importações...</div>
        ) : imports.length === 0 ? (
          <div className="mt-4 rounded-md border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
            Nenhuma importação realizada até o momento.
          </div>
        ) : (
          <div className="mt-4 overflow-hidden rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Competência</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Colaboradores</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Proventos</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Deduções</th>
                  <th className="px-4 py-2 text-right font-semibold text-gray-700">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {imports.map((item) => {
                  const totals = totalsByImport[item.id] ?? { earnings: 0, deductions: 0 };
                  return (
                    <tr key={item.id}>
                      <td className="px-4 py-3 text-gray-900">
                        <Link
                          to={`/expenses/payroll/${item.id}`}
                          className="text-primary-700 underline underline-offset-2 hover:text-primary-800"
                        >
                          {item.competence ?? item.id}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{item.collaborators.length}</td>
                      <td className="px-4 py-3 text-gray-700">{formatCurrency(totals.earnings)}</td>
                      <td className="px-4 py-3 text-gray-700">{formatCurrency(totals.deductions)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          disabled={deletingId === item.id}
                          className="inline-flex items-center gap-1 rounded-md bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <TrashIcon className="h-4 w-4" aria-hidden />
                          {deletingId === item.id ? 'Removendo...' : 'Remover'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
